// Per-channel icons for OutagePipelinePage's stage circles. Deliberately
// NOT reproductions of each vendor's actual trademarked logo artwork —
// distinctive, hand-drawn shapes in that vendor's real brand color instead,
// the same restrained approach the channel mockup components (TeamsPostMockup,
// PagerDutyAlertMockup, etc.) already use successfully: evocative of the
// product, not a copied logo file.
import type { MockupKey } from '../lib/outageFanout';

type StepColorKey = MockupKey | 'declared';

export function StepIcon({ colorKey }: { colorKey: StepColorKey }) {
  switch (colorKey) {
    case 'declared':
      // Alert triangle.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3.5 21.5 20h-19L12 3.5z" />
          <path d="M12 9.5v5" />
          <circle cx="12" cy="17.2" r="0.9" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'pagerduty':
      // Bell — paging/alerting.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" stroke="none">
          <path d="M12 2.5a1.4 1.4 0 0 1 1.4 1.4v.6c3 .6 5.1 3.2 5.1 6.4v3.9l1.7 2.7c.3.5 0 1.2-.6 1.2H5.4c-.6 0-1-.7-.6-1.2l1.7-2.7v-3.9c0-3.2 2.1-5.8 5.1-6.4v-.6A1.4 1.4 0 0 1 12 2.5z" />
          <path d="M9.4 20.2a2.6 2.6 0 0 0 5.2 0z" />
        </svg>
      );
    case 'teams':
      // Chat bubble with an unread dot — "posted a message".
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 5.5h16v10.2H9.8L5.6 19v-3.3H4V5.5z" />
          <circle cx="17.2" cy="6.3" r="2.6" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'servicenow':
      // Ticket stub.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a1.6 1.6 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a1.6 1.6 0 0 0 0-4V8z" />
          <path d="M9 6.3v11.4" strokeDasharray="2.2 2.2" />
        </svg>
      );
    case 'ai-report':
      // Four-point sparkle.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" stroke="none">
          <path d="M12 2c.6 3.4 1.9 5.9 4.4 7.4C19 10.9 21.4 11.3 22 12c-.6.7-3 1.1-5.6 2.6-2.5 1.5-3.8 4-4.4 7.4-.6-3.4-1.9-5.9-4.4-7.4C5 13.1 2.6 12.7 2 12c.6-.7 3-1.1 5.6-2.6C10.1 7.9 11.4 5.4 12 2z" />
        </svg>
      );
    case 'email':
      // Envelope.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5.5" width="18" height="13" rx="1.6" />
          <path d="M4 6.5 12 13l8-6.5" />
        </svg>
      );
    case 'ftb-notification':
      // Chat bubble — same shape as 'teams' above, since this step literally
      // is the IC/NOC Group Chat notification landing in Teams.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 5.5h16v10.2H9.8L5.6 19v-3.3H4V5.5z" />
          <circle cx="17.2" cy="6.3" r="2.6" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'ftb-snow-lookup':
      // Ticket stub — same shape as 'servicenow' above, since this step is
      // a real SNOW Location table lookup.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a1.6 1.6 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a1.6 1.6 0 0 0 0-4V8z" />
          <path d="M9 6.3v11.4" strokeDasharray="2.2 2.2" />
        </svg>
      );
    case 'ftb-sharepoint-lookup':
      // Document library — SharePoint's own "shared files" motif.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 6a1.5 1.5 0 0 1 1.5-1.5h4l1.5 2h7.5A1.5 1.5 0 0 1 20 8v10a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18V6z" />
          <path d="M8 12.5h8M8 15.5h5" />
        </svg>
      );
    case 'ftb-requirement':
      // Checklist — evaluating the FTB requirement rules.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="3.5" width="16" height="17" rx="1.6" />
          <path d="m7.5 9 1.4 1.4L11.5 7.7" />
          <path d="M13.5 8.7h4" />
          <path d="m7.5 15 1.4 1.4L11.5 13.7" />
          <path d="M13.5 14.7h4" />
        </svg>
      );
    case 'ftb-notice':
      // Bulletin/notice sheet.
      return (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 3.5h9l3 3v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1z" />
          <path d="M15 3.5v3h3" />
          <path d="M8 12.5h8M8 15.5h8M8 18h5" />
        </svg>
      );
    default:
      return null;
  }
}

// Small corner badge marking a step 'done' — the channel icon stays visible
// (unlike a generic checkmark replacing it), this just confirms completion.
export function DoneBadge() {
  return (
    <svg className="pipeline-stage-donebadge" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
      <circle cx="8" cy="8" r="8" fill="var(--ok)" />
      <path d="M4.5 8.3 6.8 10.5 11.5 5.5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
